import { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  MapPin,
  Sparkles,
} from 'lucide-react';
import { useRouter } from '../router/MarketplaceRouter';
import {
  categories,
  getVendorById,
  getServicesByCategory,
} from '../data/fixtures';
import {
  Button,
  PageSection,
  RatingLine,
  VerifiedBadge,
  Avatar,
} from '../shell/ui';

// ─── Request creation ──────────────────────────────────────────────────────

export function RequestCreateScreen() {
  const { navigate, back } = useRouter();
  const [step, setStep] = useState(1);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [brief, setBrief] = useState('');
  const [budget, setBudget] = useState('10000-20000');
  const [timeline, setTimeline] = useState('flexible');
  const [address, setAddress] = useState('');

  const canContinue =
    (step === 1 && !!categoryId) ||
    (step === 2 && brief.trim().length > 20) ||
    (step === 3 && !!address);

  return (
    <PageSection paddingY="3.5rem">
      <div
        style={{
          maxWidth: '780px',
          margin: '0 auto',
        }}
      >
        {/* Progress */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '2.5rem',
          }}
        >
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                flex: 1,
              }}
            >
              <div
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '999px',
                  background:
                    n <= step ? 'var(--color-accent-rosegold)' : 'var(--color-background-surface)',
                  color: n <= step ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                  border: '1px solid var(--color-border-medium)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {n < step ? <Check size={13} /> : n}
              </div>
              {n < 3 && (
                <div
                  style={{
                    flex: 1,
                    height: '1px',
                    background:
                      n < step ? 'var(--color-accent-rosegold)' : 'var(--color-border-light)',
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <div
          style={{
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: 'var(--color-accent-rosegold)',
            fontWeight: 500,
            marginBottom: '0.75rem',
          }}
        >
          Step {step} of 3
        </div>
        <h1
          style={{
            fontFamily: 'var(--font-family-display)',
            fontSize: 'clamp(1.875rem, 3.5vw, 2.75rem)',
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: 0,
            marginBottom: '2rem',
          }}
        >
          {step === 1 && 'What kind of work do you need?'}
          {step === 2 && (
            <>
              Tell us <span style={{ color: 'var(--color-accent-rosegold)' }}>what you have in mind</span>.
            </>
          )}
          {step === 3 && 'Where and when?'}
        </h1>

        {/* STEP 1 — category */}
        {step === 1 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.75rem',
            }}
            className="req-cat-grid"
          >
            {categories.map((c) => {
              const active = categoryId === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCategoryId(c.id)}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    padding: '1.25rem 1.125rem',
                    background: active ? 'var(--color-accent-rosegold-50)' : 'var(--color-background-card)',
                    border: `1px solid ${active ? 'var(--color-accent-rosegold)' : 'var(--color-border-light)'}`,
                    borderRadius: 'var(--radius-lg)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}
                >
                  <div
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: 'var(--radius-md)',
                      background: `url(${c.image}) center/cover no-repeat`,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontFamily: 'var(--font-family-display)',
                        fontSize: '1rem',
                        fontWeight: 400,
                        color: 'var(--color-text-primary)',
                        lineHeight: 1.2,
                      }}
                    >
                      {c.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                      {c.serviceCount} services
                    </div>
                  </div>
                </button>
              );
            })}
            <style>{`
              @media (max-width: 780px) { .req-cat-grid { grid-template-columns: repeat(2, 1fr) !important; } }
              @media (max-width: 520px) { .req-cat-grid { grid-template-columns: 1fr !important; } }
            `}</style>
          </div>
        )}

        {/* STEP 2 — brief */}
        {step === 2 && (
          <>
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Describe the work in plain language. Square meters, rooms, finishes, anything that comes to mind. Bad spelling welcome."
              rows={8}
              style={{
                width: '100%',
                padding: '1.25rem 1.25rem',
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-medium)',
                borderRadius: 'var(--radius-lg)',
                fontFamily: 'var(--font-family-body)',
                fontSize: '1rem',
                lineHeight: 1.6,
                resize: 'vertical',
                color: 'var(--color-text-primary)',
              }}
            />
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem 1.25rem',
                background: 'var(--color-accent-rosegold-50)',
                border: '1px solid var(--color-accent-rosegold-200)',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.875rem',
                color: 'var(--color-text-secondary)',
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
                lineHeight: 1.5,
              }}
            >
              <Sparkles size={16} style={{ color: 'var(--color-accent-rosegold)', marginTop: '2px', flexShrink: 0 }} />
              <span>
                <strong style={{ color: 'var(--color-text-primary)' }}>Tip.</strong> A short brief
                is fine. We’ll follow up over messaging if a vendor needs more detail.
              </span>
            </div>
          </>
        )}

        {/* STEP 3 — where / when / budget */}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.5rem',
                }}
              >
                Address or area
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.625rem',
                  padding: '0.875rem 1rem',
                  background: 'var(--color-background-card)',
                  border: '1px solid var(--color-border-medium)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <MapPin size={16} style={{ color: 'var(--color-text-muted)' }} />
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g. Nasimi, Icherisheher, Port Baku"
                  style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: '0.9375rem',
                    color: 'var(--color-text-primary)',
                    fontFamily: 'var(--font-family-body)',
                  }}
                />
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.5rem',
                }}
              >
                Budget range (AZN)
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  ['0-5000', 'Under 5,000'],
                  ['5000-10000', '5 – 10k'],
                  ['10000-20000', '10 – 20k'],
                  ['20000-50000', '20 – 50k'],
                  ['50000+', '50k+'],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => setBudget(v)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '0.625rem 1rem',
                      background: budget === v ? 'var(--color-text-primary)' : 'var(--color-background-card)',
                      color: budget === v ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '999px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.5rem',
                }}
              >
                Timeline
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  ['urgent', 'ASAP'],
                  ['month', 'Within a month'],
                  ['quarter', 'Within 3 months'],
                  ['flexible', 'Flexible'],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => setTimeline(v)}
                    style={{
                      all: 'unset',
                      cursor: 'pointer',
                      padding: '0.625rem 1rem',
                      background: timeline === v ? 'var(--color-text-primary)' : 'var(--color-background-card)',
                      color: timeline === v ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                      border: '1px solid var(--color-border-light)',
                      borderRadius: '999px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Nav buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '2.5rem',
            paddingTop: '2rem',
            borderTop: '1px solid var(--color-border-light)',
          }}
        >
          <Button
            variant="ghost"
            onClick={() => (step === 1 ? back() : setStep((s) => s - 1))}
            leftIcon={<ArrowLeft size={15} />}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          <Button
            disabled={!canContinue}
            onClick={() => (step === 3 ? navigate('offers') : setStep((s) => s + 1))}
            rightIcon={<ArrowRight size={15} />}
            style={{ opacity: canContinue ? 1 : 0.5, cursor: canContinue ? 'pointer' : 'not-allowed' }}
          >
            {step === 3 ? 'Submit request' : 'Continue'}
          </Button>
        </div>
      </div>
    </PageSection>
  );
}

// ─── Offers compare ────────────────────────────────────────────────────────

export function OffersScreen() {
  const { navigate } = useRouter();
  const renoServices = getServicesByCategory('renovation').slice(0, 3);

  const mockOffers = renoServices.map((s, i) => {
    const vendor = getVendorById(s.vendorId)!;
    return {
      id: `offer-${i}`,
      vendor,
      service: s,
      quote: s.priceFrom + i * 1200,
      duration: ['8 weeks', '10 weeks', '12 weeks'][i % 3],
      message:
        'We reviewed your brief. Happy to walk the site this week and send a fixed-price scope by Monday.',
    };
  });

  return (
    <>
      <section
        style={{
          background: 'var(--color-background-surface)',
          borderBottom: '1px solid var(--color-border-light)',
          padding: '3.5rem 0 2.75rem',
        }}
      >
        <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div
            style={{
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--color-accent-rosegold)',
              fontWeight: 500,
              marginBottom: '0.875rem',
            }}
          >
            Your request · 3 offers
          </div>
          <h1
            style={{
              fontFamily: 'var(--font-family-display)',
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              margin: 0,
              marginBottom: '0.75rem',
              maxWidth: '620px',
            }}
          >
            Three vendors. <span style={{ color: 'var(--color-accent-rosegold)' }}>Side by side.</span>
          </h1>
          <p
            style={{
              fontSize: '1.0625rem',
              color: 'var(--color-text-secondary)',
              maxWidth: '620px',
              margin: 0,
            }}
          >
            Compare offers in one view. Reply to any vendor without leaving Baza.
          </p>
        </div>
      </section>

      <PageSection paddingY="3rem">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1.25rem',
          }}
          className="offers-grid"
        >
          {mockOffers.map((o) => (
            <div
              key={o.id}
              style={{
                background: 'var(--color-background-card)',
                border: '1px solid var(--color-border-light)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  padding: '1.5rem 1.5rem 1.25rem',
                  borderBottom: '1px solid var(--color-border-light)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
                  <Avatar src={o.vendor.avatar} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{o.vendor.name}</span>
                      {o.vendor.verified && <VerifiedBadge />}
                    </div>
                    <RatingLine rating={o.vendor.rating} reviewCount={o.vendor.reviewCount} />
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-muted)',
                    lineHeight: 1.5,
                  }}
                >
                  {o.vendor.headline}
                </div>
              </div>

              <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: 'var(--color-text-muted)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Quote
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontSize: '1.75rem',
                      fontWeight: 400,
                      color: 'var(--color-text-primary)',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {o.quote.toLocaleString()} ₼
                  </div>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  <Clock size={13} /> {o.duration}
                </div>
                <p
                  style={{
                    fontSize: '0.9375rem',
                    lineHeight: 1.55,
                    color: 'var(--color-text-secondary)',
                    margin: 0,
                    marginTop: '0.25rem',
                    flex: 1,
                  }}
                >
                  “{o.message}”
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <Button size="sm" style={{ flex: 1 }} onClick={() => navigate('messages')}>
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    style={{ flex: 1 }}
                    onClick={() => navigate('messages')}
                  >
                    Message
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <style>{`
          @media (max-width: 1000px) { .offers-grid { grid-template-columns: repeat(2, 1fr) !important; } }
          @media (max-width: 640px) { .offers-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </PageSection>
    </>
  );
}

// ─── Messages stub ─────────────────────────────────────────────────────────

export function MessagesScreen() {
  const { navigate } = useRouter();
  const mockThreads = [
    { id: '1', vendor: getVendorById('atelier-nord')!, preview: 'Happy to walk the site Thursday…', unread: 2 },
    { id: '2', vendor: getVendorById('meydan-works')!, preview: 'Confirming the panel model and…', unread: 0 },
    { id: '3', vendor: getVendorById('studio-lumen')!, preview: 'Here are the lighting notes we spoke about.', unread: 0 },
  ];

  return (
    <PageSection paddingY="3rem">
      <h1
        style={{
          fontFamily: 'var(--font-family-display)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 400,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          margin: 0,
          marginBottom: '2rem',
        }}
      >
        Messages
      </h1>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '340px 1fr',
          gap: '1.25rem',
          alignItems: 'stretch',
          minHeight: '560px',
        }}
        className="msg-layout"
      >
        {/* Thread list */}
        <div
          style={{
            background: 'var(--color-background-card)',
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
          }}
        >
          {mockThreads.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate('vendor', { vendorId: t.vendor.id })}
              style={{
                all: 'unset',
                cursor: 'pointer',
                width: '100%',
                padding: '1rem 1.25rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.875rem',
                borderBottom: '1px solid var(--color-border-light)',
              }}
            >
              <Avatar src={t.vendor.avatar} size={44} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.25rem',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{t.vendor.name}</span>
                  {t.unread > 0 && (
                    <span
                      style={{
                        background: 'var(--color-accent-rosegold)',
                        color: 'var(--color-text-inverse)',
                        fontSize: '0.6875rem',
                        fontWeight: 600,
                        padding: '0.125rem 0.4375rem',
                        borderRadius: '999px',
                      }}
                    >
                      {t.unread}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: '0.8125rem',
                    color: 'var(--color-text-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {t.preview}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Empty state */}
        <div
          style={{
            background: 'var(--color-background-card)',
            border: '1px solid var(--color-border-light)',
            borderRadius: 'var(--radius-lg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '3rem',
          }}
        >
          <div style={{ maxWidth: '360px' }}>
            <div
              style={{
                fontFamily: 'var(--font-family-display)',
                fontStyle: 'italic',
                fontSize: '1.5rem',
                color: 'var(--color-text-secondary)',
                marginBottom: '0.75rem',
              }}
            >
              Select a conversation
            </div>
            <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-muted)', margin: 0 }}>
              Every reply from every vendor, in one place.
            </p>
          </div>
        </div>

        <style>{`
          @media (max-width: 780px) {
            .msg-layout { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </div>
    </PageSection>
  );
}
